/* eslint-disable no-underscore-dangle */
import {
  Input as ChakraInput,
  InputAddon as ChakraInputAddon,
  Group as ChakraGroup,
  InputGroup as ChakraInputGroup,
} from "@chakra-ui/react";
import type {
  ComponentProps,
  CSSProperties,
  ReactElement,
  ReactNode,
} from "react";
import { forwardRef, Children, isValidElement } from "react";
import spacing from "@shared/theme/props/spacing";
import Box, { type BoxProps } from "@ui-kit/layout/Box/Box.web";
import Text, { TextProps } from "../../typography/Text/Text.web";

type ChakraInputProps = ComponentProps<typeof ChakraInput>;

type TruncateCss = {
  overflow: "hidden";
  textOverflow: "ellipsis";
  whiteSpace: "nowrap";
};

// CSS to hide number input spinners
const hideNumberSpinnersCss = {
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "&[type=number]": {
    MozAppearance: "textfield",
  },
};

export type InputProps = Omit<ChakraInputProps, "variant"> & {
  required?: boolean;
  variant?: "outline" | "filled" | "flushed" | "unstyled" | "subtle";

  // v1/v2 backward-compat
  isTruncated?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  invalid?: boolean;
  /** Chakra v1/v2 compatibility */
  errorBorderColor?: string;
};

/**
 * Basic Input atom component
 */
const InputBase = forwardRef<HTMLInputElement, InputProps>(function Input(
  props,
  ref
) {
  const {
    variant,
    isDisabled,
    isReadOnly,
    isRequired,
    isInvalid,
    isTruncated,
    invalid,
    disabled,
    readOnly,
    required,
    errorBorderColor,
    css,
    ...rest
  } = props;

  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const readOnlyValue = readOnly !== undefined ? readOnly : isReadOnly;
  const requiredValue = required !== undefined ? required : isRequired;
  const invalidValue = invalid !== undefined ? invalid : isInvalid;
  const invalidBorderColor =
    errorBorderColor ?? "var(--chakra-colors-secondary-600)";

  const truncateCss: TruncateCss | undefined = isTruncated
    ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
    : undefined;

  // Merge all CSS: user css, truncate css, and hide number spinners
  const mergedCss = {
    ...hideNumberSpinnersCss,
    ...(truncateCss ?? {}),
    ...(typeof css === "object" && css !== null ? css : {}),
  } as ChakraInputProps["css"];

  if (variant === "flushed") {
    return (
      <ChakraInput
        ref={ref}
        variant="flushed"
        borderColor="border"
        color="fg"
        className="chakra-input"
        _placeholder={{ color: "fg.muted", opacity: 1 }}
        _focus={{ borderBottom: "2px solid var(--chakra-colors-primary-600)" }}
        _invalid={{ borderColor: invalidBorderColor }}
        disabled={disabledValue}
        readOnly={readOnlyValue}
        required={requiredValue}
        aria-invalid={invalidValue}
        css={mergedCss}
        {...rest}
      />
    );
  }

  if (variant === "unstyled") {
    return (
      <ChakraInput
        ref={ref}
        unstyled
        color="fg"
        disabled={disabledValue}
        readOnly={readOnlyValue}
        required={requiredValue}
        aria-invalid={invalidValue}
        css={mergedCss}
        {...rest}
      />
    );
  }

  // Map "filled" to "subtle" for v3 compatibility
  const v3Variant = variant === "filled" ? "subtle" : variant ?? "outline";

  return (
    <ChakraInput
      ref={ref}
      variant={v3Variant as ChakraInputProps["variant"]}
      backgroundColor="bg"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border"
      color="fg"
      paddingX="sm"
      paddingY="xs"
      className="chakra-input"
      _placeholder={{ color: "fg.muted", opacity: 1 }}
      _focus={{
        borderColor: "var(--chakra-colors-primary-600) !important",
        boxShadow: "0 0 0 1px var(--chakra-colors-primary-600) !important",
      }}
      _invalid={{
        borderColor: invalidBorderColor,
        boxShadow: `0 0 0 1px ${invalidBorderColor}`,
      }}
      disabled={disabledValue}
      readOnly={readOnlyValue}
      required={requiredValue}
      aria-invalid={invalidValue}
      css={mergedCss}
      {...rest}
    />
  );
});

// =============================================================================
// InputGroup - Uses Chakra v3's InputGroup with backward-compatible child API
// =============================================================================

export type InputGroupProps = BoxProps & {
  children?: ReactNode;
  // v1/v2 size prop
  size?: "xs" | "sm" | "md" | "lg";
};

// Marker types for identifying child components
const LEFT_ELEMENT_TYPE = Symbol("LeftElement");
const RIGHT_ELEMENT_TYPE = Symbol("RightElement");
const LEFT_ADDON_TYPE = Symbol("LeftAddon");
const RIGHT_ADDON_TYPE = Symbol("RightAddon");

interface MarkedElement {
  __inputType?: symbol;
}

// Helper to extract element content while preserving event handlers and style props
function extractElementWithHandlers(
  child: ReactElement,
  position: "start" | "end"
): ReactNode {
  const {
    children: content,
    onClick,
    onMouseDown,
    onMouseUp,
    cursor,
    "data-testid": dataTestId,
    // Style props
    p,
    px,
    py,
    pl,
    pr,
    pt,
    pb,
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
  } = child.props;

  // Class name for CSS targeting (used by desktop custom.css for Electron compatibility)
  const className =
    position === "start" ? "input-element-start" : "input-element-end";

  // Build CSS with padding values using !important to override Chakra's recipe
  // Default to spacing-3 for inline padding (matches Chakra's default)
  const defaultPadding = "var(--chakra-spacing-3)";

  // Resolve padding values - use provided or fall back to defaults
  const resolvedPl = pl ?? paddingLeft ?? px ?? paddingX ?? p ?? padding;
  const resolvedPr = pr ?? paddingRight ?? px ?? paddingX ?? p ?? padding;
  const resolvedPt = pt ?? paddingTop ?? py ?? paddingY ?? p ?? padding;
  const resolvedPb = pb ?? paddingBottom ?? py ?? paddingY ?? p ?? padding;

  const paddingCss: Record<string, string> = {};

  // Position-aware padding defaults:
  // - Start (left) elements: default paddingLeft, no paddingRight default
  // - End (right) elements: default paddingRight, no paddingLeft default
  if (position === "start") {
    paddingCss.paddingLeft =
      resolvedPl !== undefined
        ? `var(--chakra-spacing-${resolvedPl}) !important`
        : `${defaultPadding} !important`;
    // Only add paddingRight if explicitly provided
    if (resolvedPr !== undefined) {
      paddingCss.paddingRight = `var(--chakra-spacing-${resolvedPr}) !important`;
    }
  } else {
    // position === "end"
    paddingCss.paddingRight =
      resolvedPr !== undefined
        ? `var(--chakra-spacing-${resolvedPr}) !important`
        : `${defaultPadding} !important`;
    // Only add paddingLeft if explicitly provided
    if (resolvedPl !== undefined) {
      paddingCss.paddingLeft = `var(--chakra-spacing-${resolvedPl}) !important`;
    }
  }

  // Only add top/bottom if explicitly provided (default is 0)
  if (resolvedPt !== undefined)
    paddingCss.paddingTop = `var(--chakra-spacing-${resolvedPt}) !important`;
  if (resolvedPb !== undefined)
    paddingCss.paddingBottom = `var(--chakra-spacing-${resolvedPb}) !important`;

  // Always wrap to enable CSS targeting for desktop overrides
  return (
    <Box
      as="span"
      className={className}
      display="flex"
      alignItems="center"
      height="100%"
      cursor={cursor}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      data-testid={dataTestId}
      css={paddingCss}
    >
      {content}
    </Box>
  );
}

const Group = forwardRef<HTMLDivElement, InputGroupProps>(function Group(
  props,
  ref
): JSX.Element {
  const { children, ...rest } = props;

  // Parse children to extract elements/addons for v3 API
  let startElement: ReactNode;
  let endElement: ReactNode;
  let startAddon: ReactNode;
  let endAddon: ReactNode;
  let inputChild: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    const childType = child.type as unknown as MarkedElement;

    if (childType?.__inputType === LEFT_ELEMENT_TYPE) {
      startElement = extractElementWithHandlers(child, "start");
    } else if (childType?.__inputType === RIGHT_ELEMENT_TYPE) {
      endElement = extractElementWithHandlers(child, "end");
    } else if (childType?.__inputType === LEFT_ADDON_TYPE) {
      startAddon = child;
    } else if (childType?.__inputType === RIGHT_ADDON_TYPE) {
      endAddon = child;
    } else {
      // Assume the remaining child is the Input
      inputChild = child as ReactElement;
    }
  });

  // Wrap in Box to handle BoxProps, only pass valid props to ChakraInputGroup
  // If we have addons, use ChakraGroup for attached styling
  if (startAddon || endAddon) {
    return (
      <Box ref={ref} width="100%" {...rest}>
        <ChakraGroup attached width="100%">
          {startAddon}
          <ChakraInputGroup
            flex="1"
            startElement={startElement ?? undefined}
            endElement={endElement ?? undefined}
            css={{
              // Override Chakra's default padding on element wrappers - let our inner wrapper handle it
              "& > *:not(input):not(.chakra-input)": {
                padding: "0 !important",
                paddingInline: "0 !important",
              },
            }}
          >
            {inputChild}
          </ChakraInputGroup>
          {endAddon}
        </ChakraGroup>
      </Box>
    );
  }

  // No addons - wrap InputGroup in Box to handle BoxProps
  return (
    <Box ref={ref} width="100%" {...rest}>
      <ChakraInputGroup
        width="100%"
        startElement={startElement ?? undefined}
        endElement={endElement ?? undefined}
        css={{
          // Override Chakra's default padding on element wrappers - let our inner wrapper handle it
          "& > *:not(input):not(.chakra-input)": {
            padding: "0 !important",
            paddingInline: "0 !important",
          },
        }}
      >
        {inputChild}
      </ChakraInputGroup>
    </Box>
  );
});

// =============================================================================
// InputElement components - markers for Group to extract
// =============================================================================

export type InputElementProps = BoxProps & {
  pointerEvents?: CSSProperties["pointerEvents"];
  children?: ReactNode;
};

const LeftElement = forwardRef<HTMLDivElement, InputElementProps>(
  function LeftElement(props, ref): JSX.Element {
    const { pointerEvents = "none", children, ...rest } = props;

    // When used standalone (not in Group), render as positioned Box
    return (
      <Box
        ref={ref}
        position="absolute"
        left="none"
        top="none"
        height="100%"
        display="flex"
        alignItems="center"
        zIndex={2}
        style={{ pointerEvents }}
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

// Mark the component for Group to identify
(LeftElement as unknown as MarkedElement).__inputType = LEFT_ELEMENT_TYPE;

const RightElement = forwardRef<HTMLDivElement, InputElementProps>(
  function RightElement(props, ref): JSX.Element {
    const { pointerEvents = "auto", children, ...rest } = props;

    // When used standalone (not in Group), render as positioned Box
    return (
      <Box
        ref={ref}
        position="absolute"
        right="none"
        top="none"
        height="100%"
        display="flex"
        alignItems="center"
        zIndex={2}
        style={{ pointerEvents }}
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

// Mark the component for Group to identify
(RightElement as unknown as MarkedElement).__inputType = RIGHT_ELEMENT_TYPE;

// =============================================================================
// InputAddon components - use Chakra's InputAddon
// =============================================================================

export type InputAddonProps = ComponentProps<typeof ChakraInputAddon> & {
  children?: ReactNode;
};

const LeftAddon = forwardRef<HTMLDivElement, InputAddonProps>(
  function LeftAddon(props, ref): JSX.Element {
    return <ChakraInputAddon ref={ref} {...props} />;
  }
);

// Mark the component for Group to identify
(LeftAddon as unknown as MarkedElement).__inputType = LEFT_ADDON_TYPE;

const RightAddon = forwardRef<HTMLDivElement, InputAddonProps>(
  function RightAddon(props, ref): JSX.Element {
    return <ChakraInputAddon ref={ref} {...props} />;
  }
);

// Mark the component for Group to identify
(RightAddon as unknown as MarkedElement).__inputType = RIGHT_ADDON_TYPE;

// =============================================================================
// HelperText component
// =============================================================================

const HelperText = function HelperText(props: TextProps): JSX.Element {
  return <Text mt={spacing["2"]} fontSize="xs" color="fg.muted" {...props} />;
};

// =============================================================================
// Compound component assembly
// =============================================================================

const Input = InputBase as unknown as typeof InputBase & {
  Group: typeof Group;
  LeftElement: typeof LeftElement;
  RightElement: typeof RightElement;
  LeftAddon: typeof LeftAddon;
  RightAddon: typeof RightAddon;
  HelperText: typeof HelperText;
};

Input.Group = Group;
Input.LeftElement = LeftElement;
Input.RightElement = RightElement;
Input.LeftAddon = LeftAddon;
Input.RightAddon = RightAddon;
Input.HelperText = HelperText;

export default Input;

// Named exports for backward compatibility
export const InputGroup = Group;
export const InputLeftElement = LeftElement;
export const InputRightElement = RightElement;
export const InputLeftAddon = LeftAddon;
export const InputRightAddon = RightAddon;
export const InputHelperText = HelperText;
